import { graphql, useFragment } from "react-relay"
import { parseISO } from "date-fns"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { UserProvider } from "@/components/user-wrapper"
import { AppointmentClosedStatusIndicator } from "@/modules/waiting-room/components/appointment-closed-status-indicator"
import { AppointmentEditStatusIndicator } from "@/modules/waiting-room/components/appointment-edit-status-indicator"
import { appointmentStatusCell_appointment$key } from "@/modules/waiting-room/components/appointment-table/__generated__/appointmentStatusCell_appointment.graphql"
import { MissedCall } from "@/modules/waiting-room/components/appointment-table/appointment-missed-calls"

const AppointmentStatusCellFragment = graphql`
  fragment appointmentStatusCell_appointment on Appointment {
    id
    missedCallsCount
    lastMissedCallAt
    status
    isCompleted
    takenBy
    completedAt
    patient {
      patientFileLock {
        lockedAt
        lockedBy
      }
    }
  }
`

interface AppointmentStatusCellProps {
  appointmentRef: appointmentStatusCell_appointment$key
}

const AppointmentStatusCell = ({ appointmentRef }: AppointmentStatusCellProps) => {
  const appointment = useFragment<appointmentStatusCell_appointment$key>(AppointmentStatusCellFragment, appointmentRef)
  const { id, missedCallsCount, status, isCompleted, takenBy, lastMissedCallAt, completedAt } = appointment
  const { lockedAt, lockedBy } = appointment.patient.patientFileLock

  return (
    <div className="flex items-center gap-2">
      {missedCallsCount > 0 && status === "OPEN" && lockedBy === null && lastMissedCallAt !== null && (
        <MissedCall missedCallsCount={missedCallsCount} lastMissedCallAt={parseISO(lastMissedCallAt)} />
      )}
      {isCompleted && (
        <>
          <UserProvider
            userName={takenBy}
            render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={`status-last-updated-by-${id}`} />}
          />
          <AppointmentClosedStatusIndicator completedAt={completedAt} isCancelled={status === "CANCELLED"} />
        </>
      )}
      {!isCompleted && lockedBy !== null && (
        <>
          <UserProvider
            userName={lockedBy}
            render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={`status-last-updated-by-${id}`} />}
          />
          <AppointmentEditStatusIndicator lockedAt={lockedAt} />
        </>
      )}
    </div>
  )
}

export { AppointmentStatusCell }
